import { sql } from "drizzle-orm";
import {
  check,
  foreignKey,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { organisations, students, users } from "./schemaBase.js";
import { studentRepertoire } from "./repertoire.js";
import { z } from "zod";

export const CONCERT_STATUSES = ["draft", "scheduled", "cancelled", "completed"] as const;
export type ConcertStatus = (typeof CONCERT_STATUSES)[number];

const eventContentSchema = z.object({
  title: z.string().trim().min(1).max(200),
  venue: z.string().trim().min(1).max(200),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  familyDescription: z.string().trim().max(2000).nullable().optional(),
  eligibilityNotes: z.string().trim().max(500).nullable().optional(),
  responseDeadline: z.string().datetime({ offset: true }).nullable().optional(),
  capacity: z.number().int().positive().max(10000).nullable().optional(),
  ownerNotes: z.string().trim().max(4000).nullable().optional(),
});

function validateEventTimes(
  value: { startsAt: string; endsAt: string; responseDeadline?: string | null },
  context: z.RefinementCtx,
): void {
  const startsAt = Date.parse(value.startsAt);
  const endsAt = Date.parse(value.endsAt);
  if (endsAt <= startsAt) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["endsAt"],
      message: "The event must end after it starts.",
    });
  }
  if (value.responseDeadline && Date.parse(value.responseDeadline) >= startsAt) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["responseDeadline"],
      message: "The response deadline must be before the event starts.",
    });
  }
}

export const concertCreateSchema = eventContentSchema.superRefine(validateEventTimes);

export const concertUpdateSchema = eventContentSchema
  .partial()
  .extend({
    version: z.number().int().positive(),
    status: z.enum(CONCERT_STATUSES).optional(),
  })
  .superRefine((value, context) => {
    if (Object.keys(value).every((key) => key === "version")) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Change at least one event field.",
      });
    }
    if (value.startsAt && value.endsAt) validateEventTimes(value as { startsAt: string; endsAt: string }, context);
  });

export type ConcertCreateInput = z.infer<typeof concertCreateSchema>;
export type ConcertUpdateInput = z.infer<typeof concertUpdateSchema>;

export const concerts = pgTable(
  "concerts",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    venue: text("venue").notNull(),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    familyDescription: text("family_description"),
    eligibilityNotes: text("eligibility_notes"),
    responseDeadline: timestamp("response_deadline", { withTimezone: true }),
    capacity: integer("capacity"),
    ownerNotes: text("owner_notes"),
    status: text("status").$type<ConcertStatus>().notNull().default("draft"),
    version: integer("version").notNull().default(1),
    idempotencyKey: varchar("idempotency_key", { length: 36 }).notNull(),
    requestFingerprint: varchar("request_fingerprint", { length: 64 }).notNull(),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    updatedByUserId: varchar("updated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("concerts_org_creator_key_idx").on(
      table.organisationId,
      table.createdByUserId,
      table.idempotencyKey,
    ),
    index("concerts_org_starts_idx").on(table.organisationId, table.startsAt),
    check("concerts_title_check", sql.raw("length(trim(title)) between 1 and 200")),
    check("concerts_venue_check", sql.raw("length(trim(venue)) between 1 and 200")),
    check(
      "concerts_family_description_check",
      sql.raw("family_description is null or length(trim(family_description)) <= 2000"),
    ),
    check(
      "concerts_eligibility_check",
      sql.raw("eligibility_notes is null or length(trim(eligibility_notes)) <= 500"),
    ),
    check(
      "concerts_owner_notes_check",
      sql.raw("owner_notes is null or length(trim(owner_notes)) <= 4000"),
    ),
    check("concerts_time_order_check", sql.raw("ends_at > starts_at")),
    check(
      "concerts_response_deadline_check",
      sql.raw("response_deadline is null or response_deadline < starts_at"),
    ),
    check("concerts_capacity_check", sql.raw("capacity is null or capacity between 1 and 10000")),
    check(
      "concerts_status_check",
      sql.raw("status in ('draft','scheduled','cancelled','completed')"),
    ),
    check("concerts_version_check", sql.raw("version > 0")),
    check("concerts_idempotency_key_check", sql.raw("idempotency_key ~* '^[0-9a-f-]{36}$'")),
    check("concerts_request_fingerprint_check", sql.raw("request_fingerprint ~ '^[0-9a-f]{64}$'")),
  ],
);

export const concertAudit = pgTable(
  "concert_audit",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    eventId: varchar("event_id")
      .notNull()
      .references(() => concerts.id, { onDelete: "cascade" }),
    actorUserId: varchar("actor_user_id").references(() => users.id, { onDelete: "set null" }),
    action: text("action").$type<"created" | "updated" | "cancelled">().notNull(),
    eventVersion: integer("event_version").notNull(),
    previousStatus: text("previous_status").$type<ConcertStatus | null>(),
    currentStatus: text("current_status").$type<ConcertStatus>().notNull(),
    changedFields: jsonb("changed_fields").$type<string[]>().notNull().default(sql.raw("'[]'::jsonb")),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    index("concert_audit_event_time_idx").on(
      table.organisationId,
      table.eventId,
      table.createdAt,
    ),
    check(
      "concert_audit_action_check",
      sql.raw("action in ('created','updated','cancelled')"),
    ),
    check("concert_audit_version_check", sql.raw("event_version > 0")),
    check("concert_audit_fields_check", sql.raw("jsonb_typeof(changed_fields) = 'array'")),
  ],
);


export const CONCERT_INVITATION_STATUSES = ["invited", "accepted", "declined", "withdrawn"] as const;
export type ConcertInvitationStatus = (typeof CONCERT_INVITATION_STATUSES)[number];

export const concertInvitations = pgTable(
  "concert_invitations",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    eventId: varchar("event_id")
      .notNull()
      .references(() => concerts.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    invitedByUserId: varchar("invited_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    respondedByUserId: varchar("responded_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    status: text("status").$type<ConcertInvitationStatus>().notNull().default("invited"),
    version: integer("version").notNull().default(1),
    respondedAt: timestamp("responded_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("concert_invitations_org_event_student_idx").on(
      table.organisationId,
      table.eventId,
      table.studentId,
    ),
    index("concert_invitations_org_student_idx").on(
      table.organisationId,
      table.studentId,
    ),
    index("concert_invitations_event_status_idx").on(
      table.organisationId,
      table.eventId,
      table.status,
    ),
    check(
      "concert_invitations_status_check",
      sql.raw("status in ('invited','accepted','declined','withdrawn')"),
    ),
    check("concert_invitations_version_check", sql.raw("version > 0")),
  ],
);

export const concertInvitationAudit = pgTable(
  "concert_invitation_audit",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull(),
    invitationId: varchar("invitation_id").notNull(),
    actorUserId: varchar("actor_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    action: text("action").$type<ConcertInvitationStatus>().notNull(),
    invitationVersion: integer("invitation_version").notNull(),
    previousStatus: text("previous_status").$type<ConcertInvitationStatus | null>(),
    currentStatus: text("current_status").$type<ConcertInvitationStatus>().notNull(),
    changedFields: jsonb("changed_fields").$type<string[]>().notNull().default(sql.raw("'[]'::jsonb")),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    foreignKey({
      name: "concert_invitation_audit_org_fk",
      columns: [table.organisationId],
      foreignColumns: [organisations.id],
    }).onDelete("cascade"),
    foreignKey({
      name: "concert_invitation_audit_invitation_fk",
      columns: [table.invitationId],
      foreignColumns: [concertInvitations.id],
    }).onDelete("cascade"),
    index("concert_invitation_audit_event_time_idx").on(
      table.organisationId,
      table.invitationId,
      table.createdAt,
    ),
    check(
      "concert_invitation_audit_action_check",
      sql.raw("action in ('invited','accepted','declined','withdrawn')"),
    ),
    check("concert_invitation_audit_version_check", sql.raw("invitation_version > 0")),
    check(
      "concert_invitation_audit_status_check",
      sql.raw(
        "current_status in ('invited','accepted','declined','withdrawn') and " +
        "(previous_status is null or previous_status in ('invited','accepted','declined','withdrawn'))",
      ),
    ),
    check(
      "concert_invitation_audit_fields_check",
      sql.raw("jsonb_typeof(changed_fields) = 'array'"),
    ),
  ],
);


export const CONCERT_PROGRAMME_ENTRY_STATUSES = ["proposed", "selected"] as const;
export type ConcertProgrammeEntryStatus = (typeof CONCERT_PROGRAMME_ENTRY_STATUSES)[number];

export interface ConcertProgrammePublicSnapshot {
  event: { title: string; venue: string; startsAt: string; endsAt: string };
  performers: Array<{
    order: number;
    name: string;
    instrument: string | null;
    piece: string;
    composer: string | null;
    workOrCollection: string | null;
  }>;
}

export interface ConcertProgrammeStoredSnapshot {
  public: ConcertProgrammePublicSnapshot;
  entryRefs: Array<{ entryId: string; invitationId: string; studentId: string; repertoireId: string }>;
}

export const concertProgrammeEntries = pgTable(
  "concert_programme_entries",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    eventId: varchar("event_id")
      .notNull()
      .references(() => concerts.id, { onDelete: "cascade" }),
    invitationId: varchar("invitation_id")
      .notNull()
      .references(() => concertInvitations.id, { onDelete: "cascade" }),
    studentRepertoireId: varchar("student_repertoire_id"),
    status: text("status").$type<ConcertProgrammeEntryStatus>().notNull().default("proposed"),
    runningOrder: integer("running_order"),
    performerName: text("performer_name"),
    proposalSource: text("proposal_source").$type<"tutor" | "owner">().notNull(),
    proposedByUserId: varchar("proposed_by_user_id").references(() => users.id, { onDelete: "set null" }),
    version: integer("version").notNull().default(1),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    foreignKey({
      name: "concert_programme_entries_student_repertoire_id_fkey",
      columns: [table.studentRepertoireId],
      foreignColumns: [studentRepertoire.id],
    }).onDelete("set null"),
    uniqueIndex("concert_programme_entry_piece_idx").on(
      table.organisationId, table.eventId, table.invitationId, table.studentRepertoireId,
    ),
    uniqueIndex("concert_programme_running_order_idx")
      .on(table.organisationId, table.eventId, table.runningOrder)
      .where(sql.raw("status = 'selected' and running_order is not null")),
    index("concert_programme_event_status_idx").on(
      table.organisationId, table.eventId, table.status,
    ),
    check("concert_programme_entry_status_check", sql.raw("status in ('proposed','selected')")),
    check("concert_programme_entry_source_check", sql.raw("proposal_source in ('tutor','owner')")),
    check(
      "concert_programme_entry_order_check",
      sql.raw(
        "(status = 'proposed' and running_order is null and performer_name is null) or " +
          "(status = 'selected' and running_order between 1 and 200 and " +
          "performer_name is not null and length(trim(performer_name)) between 1 and 100)",
      ),
    ),
    check("concert_programme_entry_version_check", sql.raw("version > 0")),
  ],
);

export const concertProgrammeAudit = pgTable(
  "concert_programme_audit",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
    eventId: varchar("event_id").notNull().references(() => concerts.id, { onDelete: "cascade" }),
    entryId: varchar("entry_id"),
    actorUserId: varchar("actor_user_id").references(() => users.id, { onDelete: "set null" }),
    action: text("action").notNull(),
    programmeVersion: integer("programme_version"),
    changedFields: jsonb("changed_fields").$type<string[]>().notNull().default(sql.raw("'[]'::jsonb")),
    details: jsonb("details").$type<Record<string, unknown>>().notNull().default(sql.raw("'{}'::jsonb")),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    index("concert_programme_audit_event_time_idx").on(table.organisationId, table.eventId, table.createdAt),
    index("concert_programme_audit_entry_time_idx").on(table.organisationId, table.entryId, table.createdAt),
    check("concert_programme_audit_fields_check", sql.raw("jsonb_typeof(changed_fields) = 'array'")),
    check("concert_programme_audit_details_check", sql.raw("jsonb_typeof(details) = 'object'")),
  ],
);

export const concertProgrammePublications = pgTable(
  "concert_programme_publications",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
    eventId: varchar("event_id").notNull().references(() => concerts.id, { onDelete: "cascade" }),
    version: integer("version").notNull(),
    token: varchar("token", { length: 43 }).notNull(),
    tokenHash: varchar("token_hash", { length: 64 }).notNull(),
    contentHash: varchar("content_hash", { length: 64 }).notNull(),
    snapshot: jsonb("snapshot").$type<ConcertProgrammeStoredSnapshot>().notNull(),
    publishedByUserId: varchar("published_by_user_id").references(() => users.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    invalidatedAt: timestamp("invalidated_at", { withTimezone: true }),
    invalidatedReason: text("invalidated_reason"),
  },
  (table) => [
    uniqueIndex("concert_programme_publication_version_idx").on(table.organisationId, table.eventId, table.version),
    uniqueIndex("concert_programme_publication_token_hash_idx").on(table.tokenHash),
    uniqueIndex("concert_programme_publication_current_idx")
      .on(table.organisationId, table.eventId)
      .where(sql.raw("invalidated_at is null")),
    index("concert_programme_publication_event_idx").on(table.organisationId, table.eventId, table.createdAt),
    check("concert_programme_publication_version_check", sql.raw("version > 0")),
    check("concert_programme_publication_token_check", sql.raw("token ~ '^[A-Za-z0-9_-]{43}$'")),
    check("concert_programme_publication_token_hash_check", sql.raw("token_hash ~ '^[0-9a-f]{64}$'")),
    check("concert_programme_publication_content_hash_check", sql.raw("content_hash ~ '^[0-9a-f]{64}$'")),
    check(
      "concert_programme_publication_invalidation_check",
      sql.raw("(invalidated_at is null and invalidated_reason is null) or " +
        "(invalidated_at is not null and invalidated_reason is not null)"),
    ),
  ],
);

export const concertProgrammePublishRequests = pgTable(
  "concert_programme_publish_requests",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
    eventId: varchar("event_id").notNull().references(() => concerts.id, { onDelete: "cascade" }),
    publicationId: varchar("publication_id").notNull()
      .references(() => concertProgrammePublications.id, { onDelete: "cascade" }),
    idempotencyKey: varchar("idempotency_key", { length: 36 }).notNull(),
    requestFingerprint: varchar("request_fingerprint", { length: 64 }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("concert_programme_publish_request_key_idx").on(
      table.organisationId, table.eventId, table.idempotencyKey,
    ),
    index("concert_programme_publish_request_publication_idx").on(table.organisationId, table.publicationId),
    check("concert_programme_publish_request_key_check", sql.raw("idempotency_key ~* '^[0-9a-f-]{36}$'")),
    check("concert_programme_publish_request_fingerprint_check", sql.raw("request_fingerprint ~ '^[0-9a-f]{64}$'")),
  ],
);
